import { DefaultCustomizerInterfaceSettings, VARIANTS_DISPLAY_MODE_GALLERY } from './theme';

import colorsProfiles, { colorsDefault } from './color-profiles';

const defaultTheme: DefaultCustomizerInterfaceSettings = {
    theme: {
        fontFamily: {
            id: 0,
            name: 'Inter',
            styleUrl: '',
            allTextUppercase: false
        },
        colors: {
            ...colorsDefault
        },
        tools: {
            designElements: {
                defaultText: 'Design',
                defaultLongText: 'Design',
                defaultIconKey: 'design-elements',
                key: 'designElements',
                position: 0
            },
            uploadImage: {
                defaultText: 'Uploads',
                defaultLongText: 'Uploads',
                defaultIconKey: 'upload-image',
                key: 'uploadImage',
                position: 1
            },
            cliparts: {
                defaultText: 'Images gallery',
                defaultLongText: 'Images gallery',
                defaultIconKey: 'cliparts',
                key: 'cliparts',
                position: 2,
                categoriesMode: true
            },
            text: {
                defaultText: 'Text',
                defaultLongText: 'Text',
                defaultIconKey: 'text',
                key: 'text',
                position: 3
            },
            textArt: {
                defaultText: 'TextArt',
                defaultLongText: 'TextArt',
                defaultIconKey: 'textart',
                key: 'textArt',
                position: 4
            },
            background: {
                defaultText: 'Fill',
                defaultLongText: 'Fill Background',
                defaultIconKey: 'background',
                key: 'background',
                position: 5
            },
            templates: {
                defaultText: 'Templates',
                defaultLongText: 'Pre-Designed Templates',
                defaultIconKey: 'templates',
                key: 'templates',
                position: 6
            },
            collages: {
                defaultText: 'Collages',
                defaultLongText: 'Collages',
                defaultIconKey: 'collages',
                key: 'collages',
                position: 7,
                visible: true
            },
            shapes: {
                defaultText: 'Shapes',
                defaultLongText: 'Shapes',
                defaultIconKey: 'shapes',
                key: 'shapes',
                position: 8,
                visible: true
            },
            palette: {
                key: 'palette',
                visible: true
            },
            variants: {
                defaultText: 'Variants',
                defaultLongText: 'Switch Variants',
                defaultIconKey: 'switch-variant',
                key: 'variants',
                visible: true,
                displayMode: VARIANTS_DISPLAY_MODE_GALLERY
            },
            printingMethods: {
                defaultText: 'Printing Methods',
                defaultLongText: 'Printing Methods',
                defaultIconKey: 'printing-methods',
                key: 'printingMethods',
                visible: true
            },
            drafts: {
                defaultText: 'My saved designs',
                defaultLongText: 'My saved designs',
                defaultIconKey: 'drafts',
                key: 'drafts'
            },
            cart: {
                defaultText: 'Add to cart',
                defaultLongText: 'Add to cart',
                defaultIconKey: 'cart',
                key: 'cart'
            },
            quote: {
                defaultText: 'Get a quote',
                defaultLongText: 'Get a quote',
                defaultIconKey: 'quote',
                key: 'quote'
            },
            // COMPOSER only. Must stay in sync with the configurator's hardcoded fallback
            // (zakeke-configurator-ui SideBar.tsx) so the placeholder shown here is what the merchant sees.
            textAndImages: {
                defaultText: 'Text & Images',
                defaultIconKey: 'text-and-images',
                key: 'textAndImages'
            }
        },
        desktopLayout: {
            menuPosition: 'right',
            footerPosition: 'bottom',
            toolbarWidth: 68,
            addButtonVisible: true,
            addButtonOffsetX: 0,
            addButtonOffsetY: 0
        },
        mobileLayout: {
            footerPosition: 'bottom'
        },
        accessibilityWidget: {
            buttonColor: '#0073d3',
            buttonIconColor: '#FFFFFF',
            hide: false
        },
        interaction3D: {
            highlightColor: '#00A3FF',
            showAutoRotateControl: true
        },
        // Every accessibility control ships enabled. Only the ones a merchant turns off are persisted
        // (deepDiff strips defaults on save), and the customizer reads a missing key as `true`.
        accessibility: {
            canvas3dControls: true,
            movableQuickActions: true,
            layersReorderButtons: true,
            itemTransformControls: true,
            nativeMobileSelect: true,
            panelsAutofocus: true
        },
        compositionRecap: {
            isEnabled: false,
            isVisibleFromStart: false,
            canUserClose: false
        }
    },
    general: {
        showPrice: true,
        showProductQuantity: true,
        canUndoRedo: true,
        canZoomAndPan: true,
        showDashedEdge: false,
        zoomPercentageIncrement: 100,
        hasAdaptiveZoomEnabled: false,
        showResize3DPreview: false,
        width3DPreview: 220,
        height3DPreview: 200,
        allowPreview3DExpandOnContainerClick: false,
        showSideDimensions: true,
        showElementsDimensions: true,
        showSelectedElementDimensions: false,
        showAreaDimensions: false,
        unitOfMeasurement: 'cm',
        priceText: undefined,
        showSnapGuides: true,
        showProductName: true,
        forceVariantOnStart: false,
        startingPanelOptions: 'none',
        autoOpenPreview3DOnDesktop: false,
        useNarrowCurrencySymbol: false,
        showVariantName: false,
        showDuplicateButton: true,
        showLayerButtons: true,
        showFlipButtons: true,
        // = BE default of GeneralSettings.isManualSizeEnabled (false); MUST stay in sync with the customizer UI runtime fallback
        enableManuallySizeImage: false
    },
    plugins: []
};

type ColorProfile = {
    id: number;
    name: string;
} & DefaultCustomizerInterfaceSettings['theme']['colors'];

export type ZakekeEnv = 'CUSTOMIZER' | 'COMPOSER';

export type DefaultSellerUserInterface = {
    id: undefined;
    name: string | null;
    environment: ZakekeEnv;
    isDefault: boolean;
    settings: string;
    url?: string;
    userInterfaceId: number;
    productsAssociated: Array<{ productId: number }>;
    sellerUserInterfaceId?: number;
    pluginsSettings?: { readonly isPluginCopy: boolean } | null;
};
export type Settings = {
    defaultSellerUserInterface: DefaultSellerUserInterface;
    defaultSellerUserInterfaceForCreate: DefaultSellerUserInterface;
    defaultTheme: DefaultCustomizerInterfaceSettings;
    profiles: ColorProfile[];
};

// Global UserInterface ids (see the UserInterfaces table). They are rows of a per-deployment database,
// so each id is overridable at build time via env var and falls back to the value used by the Zakeke deployments.
const resolveUserInterfaceId = (value: string | undefined, fallback: number): number => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

// The NEW/default UI id per environment.
export const ENV_TO_USER_INTERFACE_ID: Record<ZakekeEnv, number> = {
    CUSTOMIZER: resolveUserInterfaceId(process.env.CUSTOMIZER_USER_INTERFACE_ID, 2),
    COMPOSER: resolveUserInterfaceId(process.env.COMPOSER_USER_INTERFACE_ID, 4)
};

// The OLD UI id per environment — written when switching back to the old interface so the storefront
// resolves the old bundle deterministically (instead of relying on the FallBackUserInterface* config).
export const ENV_TO_OLD_USER_INTERFACE_ID: Record<ZakekeEnv, number> = {
    CUSTOMIZER: resolveUserInterfaceId(process.env.CUSTOMIZER_OLD_USER_INTERFACE_ID, 1),
    COMPOSER: resolveUserInterfaceId(process.env.COMPOSER_OLD_USER_INTERFACE_ID, 3)
};

export const createDefaultSellerUserInterface = (environment: ZakekeEnv = 'CUSTOMIZER'): DefaultSellerUserInterface => ({
    id: undefined,
    name: 'Default',
    environment,
    isDefault: true,
    settings: '{}',
    userInterfaceId: ENV_TO_USER_INTERFACE_ID[environment],
    productsAssociated: []
});

export const createDefaultSellerUserInterfaceForCreate = (environment: ZakekeEnv = 'CUSTOMIZER'): DefaultSellerUserInterface => ({
    id: undefined,
    environment,
    name: '',
    isDefault: false,
    settings: '{}',
    url: '',
    userInterfaceId: ENV_TO_USER_INTERFACE_ID[environment],
    productsAssociated: []
});

export const settings: Settings = {
    defaultSellerUserInterface: createDefaultSellerUserInterface(),
    defaultSellerUserInterfaceForCreate: createDefaultSellerUserInterfaceForCreate(),
    defaultTheme,
    profiles: [...colorsProfiles]
};
